import { useEffect, useMemo, useReducer } from "react";
import { DateTime } from "luxon";
import { useAuthStore } from "service/services/shared/auth/auth-store";
import type { AdminGetRoutesResDto } from "service/api/estabrookSchemas";
import { getTimelineDate } from "../pages/dashboard/route-planner/helpers/route-start";
import {
  readLiveShiftDay,
  writeLiveShiftDay,
} from "../pages/dashboard/route-planner/helpers/live-shift-day";

export function useLiveTimelineDate(
  routes: AdminGetRoutesResDto[],
  now: DateTime,
  selectedDate?: string,
): string {
  const organizationId = useAuthStore((state) => state.organizationId);
  const [storageRevision, refresh] = useReducer(
    (value: number) => value + 1,
    0,
  );
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!event.key || event.key.startsWith("live-shift-day:")) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A table has one clock. Never mix origins from different shift dates.
  const date = selectedDate ?? routes[0]?.date?.slice(0, 10);
  const rows = useMemo(
    () => routes.filter((route) => route.date?.slice(0, 10) === date),
    [routes, date],
  );
  const inferred = getTimelineDate(rows, now);
  const live = rows.some((row) => row.state === "live");
  const saved =
    organizationId && date && live
      ? readLiveShiftDay(organizationId, date, now, inferred)
      : undefined;
  const day = saved && saved < inferred ? saved : inferred;

  // A read-only Planned/Ended view may be stale. Only successful lifecycle
  // mutations may clear/reset the shared day; observing non-live data cannot.
  useEffect(() => {
    if (date && live) writeLiveShiftDay(organizationId, date, day);
  }, [organizationId, date, live, day, storageRevision]);
  return day;
}
